import { EDoorWindowSide } from '@/editor/data/model/model';
import { globalStores } from '@/editor/stores';
import { modelManager } from '../manager/entityManager';
import editorEvent, { EditorEventName } from '../event/editorEvent';
import { DXF_BASEMAP_CONTEXT_VERSION, DxfBasemapParserService } from '@/editor/floor-plan/dxf/parser-next';
import { decompressDxfBasemapContext } from '@/editor/floor-plan/dxf/parser-next/codec';
import { uploadDxfBasemapContext } from '@/editor/floor-plan/dxf/parser-next/upload';
import type { DxfBasemapContext } from '@/editor/floor-plan/dxf/parser-next/types';
import {
  DXF_BASEMAP_CONTEXT_ASSET_FORMAT,
  FLOOR_PLAN_CONFIG_VERSION,
  EPointType,
  type IDxfFloorPlanConfig
} from '../types';
import { LATEST_VERSION, upgradeToLatest, CommonVersionData, LatestVersionSchema } from './versionData';
import { ESeparateWallType } from '../data/separateWall/SeparateWall';
import { useLoadingStore } from '@/stores/feedback';
import {
  getDxfFloorPlanHiddenLayers,
  getDxfFloorPlanRenderSolid,
  getPdfFloorPlanScale,
  isCompressedDxfFloorPlan,
  isLegacyRawDxfFloorPlan,
  normalizeFloorPlanConfig,
  toPersistedFloorPlanConfig
} from './floorPlanConfig';

let dxfFloorPlanLoadingToken = 0;

export function saveSceneScheme(): LatestVersionSchema {
  const systemParams = globalStores.paramStore.getDatas();
  // 获取 DXF 底图图层的隐藏状态
  const floorPlanConfig = toPersistedFloorPlanConfig(systemParams.floorPlanConfig, {
    dxfHiddenLayers:
      systemParams.floorPlanConfig?.type === 'dxf' ? modelManager.floorPlanManager.getDxfHiddenLayers() : undefined
  });
  const wallDatas = globalStores.wallStore.getWallDatas().map((item) => {
    return { id: item.id, baseData: item.baseData };
  });
  const roomDatas = globalStores.roomStore.getRoomDatas();
  const separateWallDatas = globalStores.separateWallStore.getDatas();
  const regionDatas = globalStores.regionStore.getRegionDatas();
  const doorWindowDatas = globalStores.modelStore.getWindowDatas().map((item) => ({
    ...item,
    side: item.side ?? EDoorWindowSide.right,
    flip: item.flip ?? false
  }));
  const furnitureDatas = globalStores.modelStore.getFurnitureDatas();
  const columnDatas = globalStores.structStore.getColumnDatas();
  const faceDatas = globalStores.faceStore.getDatas();
  const assemblyDatas = globalStores.assemblyStore.getAssemblyDatas();
  const areaDimensionDatas = globalStores.dimensionStore.getAreaDimList();
  const linearDimensionDatas = globalStores.dimensionStore.getLinearDimList();

  const schemeData = {
    version: LATEST_VERSION,
    systemParams: {
      ...systemParams,
      floorPlanConfig
    },
    wallDatas,
    roomDatas,
    separateWallDatas,
    regionDatas,
    doorWindowDatas,
    furnitureDatas,
    columnDatas,
    faceDatas,
    assemblyDatas,
    dimensionDatas: { areaDimensionDatas, linearDimensionDatas }
  };

  return schemeData;
}

export async function loadSceneScheme(schemeJson: CommonVersionData) {
  if (!schemeJson) return;

  // 历史数据迁移到最新版本
  const latestSchemeData = await upgradeToLatest(schemeJson);
  if (!latestSchemeData) return;

  beforeLoad();

  coverStoreDatas(latestSchemeData);

  afterLoaded();
}

function beforeLoad() {
  globalStores.editorStateStore.clearSelected();
}

function afterLoaded() {
  globalStores.refreshData();
  modelManager.updateEntity();
  editorEvent.emit(EditorEventName.SCHEME_LOADED); // 刷新右侧面板参数，以免展示错误

  renderFloorPlan();
}

function renderFloorPlan() {
  const floorPlan = globalStores.paramStore.floorPlanConfig;

  if (!floorPlan || !floorPlan.url) return;

  switch (floorPlan.type) {
    case 'dxf': {
      const loadingToken = startDxfFloorPlanLoading();
      renderDxfFloorPlan(floorPlan)
        .catch((error) => {
          console.warn('DXF户型图解析失败');
          console.error(error);
        })
        .finally(() => {
          finishDxfFloorPlanLoading(loadingToken);
        });
      break;
    }
    case 'pdf': {
      fetch(floorPlan.url)
        .then((res) => res.blob())
        .then((blob) => new File([blob], 'floorplan', { type: blob.type }))
        .then((file) => {
          void modelManager.floorPlanManager.renderPDF(file, getPdfFloorPlanScale(floorPlan));
        })
        .catch(() => {
          console.warn('户型图加载失败');
        });
      break;
    }
    default: {
      console.warn('不支持的户型图类型');
      break;
    }
  }
}

function startDxfFloorPlanLoading() {
  const token = ++dxfFloorPlanLoadingToken;
  useLoadingStore.getState().startLoading({
    variant: 'transparent',
    hideProgressBar: true
  });
  return token;
}

function finishDxfFloorPlanLoading(token: number) {
  if (token !== dxfFloorPlanLoadingToken) return;
  useLoadingStore.getState().finishLoading();
}

async function renderDxfFloorPlan(floorPlan: IDxfFloorPlanConfig) {
  if (!floorPlan.url) {
    console.warn('DXF户型图加载失败：缺少文件地址');
    return;
  }

  const floorPlanBuffer = await fetch(floorPlan.url).then((res) => res.arrayBuffer());
  const hiddenLayers = getDxfFloorPlanHiddenLayers(floorPlan);

  if (isCompressedDxfFloorPlan(floorPlan)) {
    const basemapContext = await decompressDxfBasemapContext(floorPlanBuffer);
    const { renderContext } = await DxfBasemapParserService.instance.buildRenderContext(basemapContext);
    await modelManager.floorPlanManager.renderDXFWithContext(renderContext, hiddenLayers);
    return;
  }

  if (isLegacyRawDxfFloorPlan(floorPlan)) {
    const { basemapContext, renderContext } = await DxfBasemapParserService.instance.parseRaw(floorPlanBuffer, {
      transformOcs: false,
      renderSolid: getDxfFloorPlanRenderSolid(floorPlan)
    });
    await modelManager.floorPlanManager.renderDXFWithContext(renderContext, hiddenLayers);
    convertLegacyDxfBasemap(floorPlan, basemapContext);
    return;
  }

  console.warn('不支持的DXF底图数据格式');
}

function convertLegacyDxfBasemap(floorPlan: IDxfFloorPlanConfig, basemapContext: DxfBasemapContext) {
  uploadDxfBasemapContext(basemapContext, floorPlan.name || 'floorplan.dxf')
    .then(({ key }) => {
      const currentFloorPlan = globalStores.paramStore.floorPlanConfig;
      if (
        !currentFloorPlan ||
        currentFloorPlan.type !== 'dxf' ||
        currentFloorPlan.id !== floorPlan.id ||
        !isLegacyRawDxfFloorPlan(currentFloorPlan)
      ) {
        return;
      }

      globalStores.compositeOperation.setNotHistoryParams({
        floorPlanConfig: {
          id: key,
          type: 'dxf',
          name: currentFloorPlan.name,
          version: FLOOR_PLAN_CONFIG_VERSION,
          config: {
            assetFormat: DXF_BASEMAP_CONTEXT_ASSET_FORMAT,
            contextVersion: DXF_BASEMAP_CONTEXT_VERSION,
            compression: 'gzip',
            transformOcs: false,
            renderSolid: getDxfFloorPlanRenderSolid(currentFloorPlan),
            dxfHiddenLayers: modelManager.floorPlanManager.getDxfHiddenLayers()
          }
        }
      });
    })
    .catch((error) => {
      console.warn('旧DXF底图转换上传失败');
      console.error(error);
    });
}

function coverStoreDatas(data: LatestVersionSchema) {
  coverPointDatas(data);
  const floorPlanConfig = normalizeFloorPlanConfig(data.systemParams.floorPlanConfig);

  globalStores.paramStore.coverDatas({
    ...data.systemParams,
    separateWallType: data.systemParams.separateWallType as ESeparateWallType,
    floorPlanConfig
  });

  // const mockData = mockSchemeData();
  // globalStores.wallStore.coverDatas(
  //   mockData.wallDatas.map((item) => {
  //     item.drawWallInfo = initDrawWallInfo(item.baseData);
  //     return item;
  //   })
  // );
  // const doorWindowDatas = mockData.windowDatas.map((item) => syncDistanceTypeToCenterType(item));
  // globalStores.modelStore.coverDatas(doorWindowDatas, mockData.furnitureDatas);

  globalStores.wallStore.coverDatas(data.wallDatas);
  globalStores.roomStore.coverDatas(data.roomDatas);
  globalStores.regionStore.coverDatas(data.regionDatas);
  // @ts-expect-error 字符与枚举不兼容
  globalStores.separateWallStore.coverDatas(data.separateWallDatas);
  // @ts-expect-error 字符与枚举不兼容
  globalStores.modelStore.coverDatas(data.doorWindowDatas, data.furnitureDatas);
  globalStores.assemblyStore.coverDatas(data.assemblyDatas);
  // @ts-expect-error 字符与枚举不兼容
  globalStores.structStore.coverDatas(data.columnDatas);
  // @ts-expect-error 字符与枚举不兼容
  globalStores.faceStore.coverDatas(data.faceDatas);
  globalStores.dimensionStore.coverDatas(data.dimensionDatas);
}

function coverPointDatas(data: LatestVersionSchema) {
  globalStores.pointStore.clearWall();

  const pointData = new Map<string, Set<number>>();

  const wallPointSet = new Set<number>();
  data.faceDatas.wallFaceDatas.forEach((item) => {
    if (item.relativeInfo.startId) wallPointSet.add(item.relativeInfo.startId);
    if (item.relativeInfo.endId) wallPointSet.add(item.relativeInfo.endId);
  });
  pointData.set(EPointType.Wall, wallPointSet);

  // 先获取region -> room 映射
  const regionRoomMap = new Map<string, string>();
  data.regionDatas.forEach((item) => {
    regionRoomMap.set(item.id, item.relateRoomId);
  });
  data.faceDatas.sepWallFaceDatas.forEach((item) => {
    const regionId = item.relativeInfo.regionId;
    if (!regionId) return;

    const roomId = regionRoomMap.get(regionId);
    if (!roomId) return;

    const mapKey = `${EPointType.Sep}-${roomId}`;
    let roomPointSet = pointData.get(mapKey);
    if (!roomPointSet) {
      roomPointSet = new Set<number>();
      pointData.set(mapKey, roomPointSet);
    }

    if (item.relativeInfo.startId) roomPointSet.add(item.relativeInfo.startId);
    if (item.relativeInfo.endId) roomPointSet.add(item.relativeInfo.endId);
  });

  globalStores.pointStore.coverDatas(pointData);
}
